import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Switch,
  ScrollView,
  SafeAreaView,
  Modal,
  Pressable,
  Alert,
  ActivityIndicator,
  Platform,
  StatusBar,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  Ionicons,
  MaterialIcons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Standalone Privacy Service - No external dependencies
const StandalonePrivacyService = {
  // Initialize default privacy preferences
  getDefaultPrivacySettings: () => ({
    // Data Collection
    personalDataCollection: true,
    usageAnalytics: true,
    crashReporting: true,
    performanceData: false,
    locationTracking: false,
    deviceInformation: true,
    
    // Data Sharing
    thirdPartySharing: false,
    marketingPartners: false,
    analyticsProviders: true,
    socialMediaIntegration: false,
    advertisingNetworks: false,
    
    // Communication Preferences
    profileVisibility: 'public', // public, private, contacts
    contactByRecruiters: true,
    showOnlineStatus: true,
    shareActivityStatus: false,
    allowDirectMessages: true,
    
    // Data Retention
    dataRetentionPeriod: '2-years', // 6-months, 1-year, 2-years, indefinite
    automaticDataDeletion: true,
    keepApplicationHistory: true,
    retainSearchHistory: false,
    
    // Security & Access
    twoFactorAuth: false,
    loginNotifications: true,
    suspiciousActivityAlerts: true,
    dataAccessLogging: true,
    
    // Cookie & Tracking
    essentialCookies: true,
    functionalCookies: true,
    analyticsCookies: false,
    advertisingCookies: false,
    crossSiteTracking: false,
    
    // Account Privacy
    hideFromSearchEngines: false,
    anonymizePublicData: false,
    limitDataProcessing: false,
    optOutOfProfiling: false,
  }),

  // Load privacy settings from AsyncStorage
  loadPrivacySettings: async () => {
    try {
      const stored = await AsyncStorage.getItem('privacySettings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...StandalonePrivacyService.getDefaultPrivacySettings(), ...parsed };
      }
      return StandalonePrivacyService.getDefaultPrivacySettings();
    } catch (error) {
      console.error('Error loading privacy settings:', error);
      return StandalonePrivacyService.getDefaultPrivacySettings();
    }
  },

  // Save privacy settings to AsyncStorage
  savePrivacySettings: async (settings) => {
    try {
      await AsyncStorage.setItem('privacySettings', JSON.stringify({
        ...settings,
        lastUpdated: new Date().toISOString(),
      }));
      return true;
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      return false;
    }
  },

  // Update specific privacy setting
  updatePrivacySetting: async (key, value) => {
    try {
      const current = await StandalonePrivacyService.loadPrivacySettings();
      const updated = { ...current, [key]: value };
      await StandalonePrivacyService.savePrivacySettings(updated);
      return updated;
    } catch (error) {
      console.error('Error updating privacy setting:', error);
      return null;
    }
  },

  // Get privacy report
  generatePrivacyReport: async () => {
    try {
      const settings = await StandalonePrivacyService.loadPrivacySettings();
      const userData = await AsyncStorage.getItem('userData');
      
      return {
        reportDate: new Date().toISOString(),
        privacySettings: settings,
        dataCollected: {
          personalData: settings.personalDataCollection,
          usageData: settings.usageAnalytics,
          locationData: settings.locationTracking,
          deviceData: settings.deviceInformation,
        },
        dataSharing: {
          thirdParties: settings.thirdPartySharing,
          marketing: settings.marketingPartners,
          analytics: settings.analyticsProviders,
        },
        retentionPolicy: {
          period: settings.dataRetentionPeriod,
          autoDelete: settings.automaticDataDeletion,
        },
        userRights: {
          dataAccess: true,
          dataPortability: true,
          dataCorrection: true,
          dataDeletion: true,
          optOut: settings.optOutOfProfiling,
        },
      };
    } catch (error) {
      console.error('Error generating privacy report:', error);
      return null;
    }
  },

  // Request data deletion
  requestDataDeletion: async (categories) => {
    try {
      const deletionRequest = {
        requestId: Date.now().toString(),
        requestDate: new Date().toISOString(),
        categories,
        status: 'pending',
        estimatedCompletion: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      };
      
      await AsyncStorage.setItem('dataDeletionRequest', JSON.stringify(deletionRequest));
      return deletionRequest;
    } catch (error) {
      console.error('Error requesting data deletion:', error);
      return null;
    }
  },
};

export default function PrivacySettingsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const navigation = useNavigation();

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({});

  // Modal states
  const [showProfileVisibilityModal, setShowProfileVisibilityModal] = useState(false);
  const [showDataRetentionModal, setShowDataRetentionModal] = useState(false);
  const [showDataDeletionModal, setShowDataDeletionModal] = useState(false);
  const [showPrivacyReportModal, setShowPrivacyReportModal] = useState(false);

  // Data deletion categories
  const [deletionCategories, setDeletionCategories] = useState({
    personalInfo: false,
    applicationHistory: false,
    searchHistory: false,
    communicationHistory: false,
    analyticsData: false,
    allData: false,
  });

  // Available options
  const profileVisibilityOptions = [
    { value: 'public', label: 'Public', description: 'Visible to everyone' },
    { value: 'private', label: 'Private', description: 'Only visible to you' },
    { value: 'contacts', label: 'Contacts Only', description: 'Visible to your connections' },
  ];

  const dataRetentionOptions = [
    { value: '6-months', label: '6 Months', description: 'Data deleted after 6 months' },
    { value: '1-year', label: '1 Year', description: 'Data deleted after 1 year' },
    { value: '2-years', label: '2 Years', description: 'Data deleted after 2 years' },
    { value: 'indefinite', label: 'Indefinite', description: 'Data kept until manually deleted' },
  ];

  useEffect(() => {
    loadPrivacySettings();
  }, []);

  const loadPrivacySettings = async () => {
    try {
      setIsLoading(true);
      const settings = await StandalonePrivacyService.loadPrivacySettings();
      setPrivacySettings(settings);
    } catch (error) {
      console.error('Error loading privacy settings:', error);
      Alert.alert('Error', 'Failed to load privacy settings. Using default values.');
      setPrivacySettings(StandalonePrivacyService.getDefaultPrivacySettings());
    } finally {
      setIsLoading(false);
    }
  };

  const updatePrivacySetting = async (key, value) => {
    try {
      setIsSaving(true);
      const updated = await StandalonePrivacyService.updatePrivacySetting(key, value);
      if (updated) {
        setPrivacySettings(updated);
      } else {
        Alert.alert('Error', 'Failed to save privacy setting. Please try again.');
      }
    } catch (error) {
      console.error('Error updating privacy setting:', error);
      Alert.alert('Error', 'Failed to save privacy setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePrivacyReport = async () => {
    try {
      const report = await StandalonePrivacyService.generatePrivacyReport();
      if (report) {
        Alert.alert(
          'Privacy Report Generated',
          'Your privacy report has been generated and will be sent to your email address within 24 hours.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert('Error', 'Failed to generate privacy report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating privacy report:', error);
      Alert.alert('Error', 'Failed to generate privacy report. Please try again.');
    }
  };

  const handleDataDeletionRequest = async () => {
    const selectedCategories = Object.keys(deletionCategories).filter(
      key => deletionCategories[key]
    );

    if (selectedCategories.length === 0) {
      Alert.alert('Error', 'Please select at least one data category to delete.');
      return;
    }

    Alert.alert(
      'Confirm Data Deletion',
      `Are you sure you want to delete the selected data categories? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const request = await StandalonePrivacyService.requestDataDeletion(selectedCategories);
              if (request) {
                setShowDataDeletionModal(false);
                setDeletionCategories({
                  personalInfo: false,
                  applicationHistory: false,
                  searchHistory: false,
                  communicationHistory: false,
                  analyticsData: false,
                  allData: false,
                });
                Alert.alert(
                  'Deletion Request Submitted',
                  `Your data deletion request has been submitted. Request ID: ${request.requestId}\n\nEstimated completion: ${new Date(request.estimatedCompletion).toLocaleDateString()}`
                );
              } else {
                Alert.alert('Error', 'Failed to submit deletion request. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting deletion request:', error);
              Alert.alert('Error', 'Failed to submit deletion request. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handleContactDataProtectionOfficer = () => {
    Alert.alert(
      'Contact Data Protection Officer',
      'How would you like to contact our Data Protection Officer?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Email',
          onPress: () => {
            Linking.openURL('mailto:dpo@jobeng.ai?subject=Privacy Inquiry');
          },
        },
        {
          text: 'Phone',
          onPress: () => {
            Linking.openURL('tel:+442071234567');
          },
        },
      ]
    );
  };

  // Render a setting item with a switch
  const renderSwitchSetting = (icon, iconColor, title, description, value, onValueChange, isImportant = false) => (
    <View style={[styles.settingItem, isImportant && styles.importantSetting]}>
      <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
        <Ionicons name={icon} size={22} color={iconColor} />
      </View>
      <View style={styles.settingContent}>
        <Text style={[styles.settingTitle, isImportant && styles.importantTitle]}>{title}</Text>
        {description && <Text style={styles.settingDescription}>{description}</Text>}
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: '#E2E8F0', true: isImportant ? '#ef444450' : '#0e4e9650' }}
        thumbColor={value ? (isImportant ? '#ef4444' : '#0e4e96') : '#f4f3f4'}
        ios_backgroundColor="#E2E8F0"
        disabled={isSaving}
      />
    </View>
  );

  // Render a setting item with a chevron (for navigation)
  const renderChevronSetting = (icon, iconColor, title, description, onPress) => (
    <TouchableOpacity style={styles.settingItem} onPress={onPress}>
      <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
        <Ionicons name={icon} size={22} color={iconColor} />
      </View>
      <View style={styles.settingContent}>
        <Text style={styles.settingTitle}>{title}</Text>
        {description && <Text style={styles.settingDescription}>{description}</Text>}
      </View>
      <Ionicons name="chevron-forward" size={20} color="#64748B" />
    </TouchableOpacity>
  );

  // Render a setting item with a value display
  const renderValueSetting = (icon, iconColor, title, value, onPress) => (
    <TouchableOpacity style={styles.settingItem} onPress={onPress}>
      <View style={[styles.settingIconContainer, { backgroundColor: iconColor + '20' }]}>
        <Ionicons name={icon} size={22} color={iconColor} />
      </View>
      <View style={styles.settingContent}>
        <Text style={styles.settingTitle}>{title}</Text>
      </View>
      <View style={styles.settingValueContainer}>
        <Text style={styles.settingValue}>{value}</Text>
        <Ionicons name="chevron-forward" size={20} color="#64748B" />
      </View>
    </TouchableOpacity>
  );

  // Render modal for selections
  const renderSelectionModal = (visible, setVisible, title, options, currentValue, setValue) => (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setVisible(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{title}</Text>
            <Pressable style={styles.closeButton} onPress={() => setVisible(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </Pressable>
          </View>
          <ScrollView style={styles.modalScrollView}>
            {options.map(option => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.modalOption,
                  currentValue === option.value && styles.modalOptionSelected,
                ]}
                onPress={() => {
                  setValue(option.value);
                  setVisible(false);
                }}
              >
                <View style={styles.modalOptionContent}>
                  <Text
                    style={[
                      styles.modalOptionText,
                      currentValue === option.value && styles.modalOptionTextSelected,
                    ]}
                  >
                    {option.label}
                  </Text>
                  <Text style={styles.modalOptionDescription}>{option.description}</Text>
                </View>
                {currentValue === option.value && (
                  <Ionicons name="checkmark" size={20} color="#0e4e96" />
                )}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading privacy settings...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Privacy Settings</Text>
        <View style={styles.headerRight}>
          {isSaving && <ActivityIndicator size={20} color="#FFFFFF" />}
        </View>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {/* Privacy Overview */}
        <View style={styles.overviewCard}>
          <View style={styles.overviewHeader}>
            <Ionicons name="shield-checkmark" size={32} color="#0e4e96" />
            <Text style={styles.overviewTitle}>Your Privacy Matters</Text>
          </View>
          <Text style={styles.overviewText}>
            Control how your data is collected, used, and shared. You have the right to access, 
            correct, and delete your personal information at any time.
          </Text>
        </View>

        {/* Data Collection Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Data Collection</Text>

          {renderSwitchSetting(
            'person-outline',
            '#0e4e96',
            'Personal Data Collection',
            'Allow collection of profile and contact information',
            privacySettings.personalDataCollection || false,
            (value) => updatePrivacySetting('personalDataCollection', value)
          )}

          {renderSwitchSetting(
            'analytics-outline',
            '#0e4e96',
            'Usage Analytics',
            'Help improve the app by sharing usage data',
            privacySettings.usageAnalytics || false,
            (value) => updatePrivacySetting('usageAnalytics', value)
          )}

          {renderSwitchSetting(
            'bug-outline',
            '#0e4e96',
            'Crash Reporting',
            'Automatically send crash reports to help fix issues',
            privacySettings.crashReporting || false,
            (value) => updatePrivacySetting('crashReporting', value)
          )}

          {renderSwitchSetting(
            'speedometer-outline',
            '#0e4e96',
            'Performance Data',
            'Collect app performance and optimization data',
            privacySettings.performanceData || false,
            (value) => updatePrivacySetting('performanceData', value)
          )}

          {renderSwitchSetting(
            'location-outline',
            '#ef4444',
            'Location Tracking',
            'Allow location-based job recommendations',
            privacySettings.locationTracking || false,
            (value) => updatePrivacySetting('locationTracking', value),
            true
          )}

          {renderSwitchSetting(
            'phone-portrait-outline',
            '#0e4e96',
            'Device Information',
            'Collect device type and operating system info',
            privacySettings.deviceInformation || false,
            (value) => updatePrivacySetting('deviceInformation', value)
          )}
        </View>

        {/* Data Sharing Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Data Sharing</Text>

          {renderSwitchSetting(
            'share-outline',
            '#ef4444',
            'Third-Party Sharing',
            'Share data with trusted third-party services',
            privacySettings.thirdPartySharing || false,
            (value) => updatePrivacySetting('thirdPartySharing', value),
            true
          )}

          {renderSwitchSetting(
            'megaphone-outline',
            '#ef4444',
            'Marketing Partners',
            'Share data with marketing and advertising partners',
            privacySettings.marketingPartners || false,
            (value) => updatePrivacySetting('marketingPartners', value),
            true
          )}

          {renderSwitchSetting(
            'bar-chart-outline',
            '#f59e0b',
            'Analytics Providers',
            'Share anonymized data with analytics services',
            privacySettings.analyticsProviders || false,
            (value) => updatePrivacySetting('analyticsProviders', value)
          )}

          {renderSwitchSetting(
            'logo-facebook',
            '#ef4444',
            'Social Media Integration',
            'Allow social media platforms to access your data',
            privacySettings.socialMediaIntegration || false,
            (value) => updatePrivacySetting('socialMediaIntegration', value),
            true
          )}

          {renderSwitchSetting(
            'tv-outline',
            '#ef4444',
            'Advertising Networks',
            'Share data with advertising networks for targeted ads',
            privacySettings.advertisingNetworks || false,
            (value) => updatePrivacySetting('advertisingNetworks', value),
            true
          )}
        </View>

        {/* Communication Privacy Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Communication Privacy</Text>

          {renderValueSetting(
            'eye-outline',
            '#0e4e96',
            'Profile Visibility',
            profileVisibilityOptions.find(opt => opt.value === privacySettings.profileVisibility)?.label || 'Public',
            () => setShowProfileVisibilityModal(true)
          )}

          {renderSwitchSetting(
            'briefcase-outline',
            '#0e4e96',
            'Contact by Recruiters',
            'Allow recruiters to contact you directly',
            privacySettings.contactByRecruiters || false,
            (value) => updatePrivacySetting('contactByRecruiters', value)
          )}

          {renderSwitchSetting(
            'radio-outline',
            '#0e4e96',
            'Show Online Status',
            'Display when you are online to other users',
            privacySettings.showOnlineStatus || false,
            (value) => updatePrivacySetting('showOnlineStatus', value)
          )}

          {renderSwitchSetting(
            'pulse-outline',
            '#0e4e96',
            'Share Activity Status',
            'Share your job search activity with connections',
            privacySettings.shareActivityStatus || false,
            (value) => updatePrivacySetting('shareActivityStatus', value)
          )}

          {renderSwitchSetting(
            'chatbubble-outline',
            '#0e4e96',
            'Allow Direct Messages',
            'Allow other users to send you direct messages',
            privacySettings.allowDirectMessages || false,
            (value) => updatePrivacySetting('allowDirectMessages', value)
          )}
        </View>

        {/* Data Retention Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Data Retention</Text>

          {renderValueSetting(
            'time-outline',
            '#0e4e96',
            'Data Retention Period',
            dataRetentionOptions.find(opt => opt.value === privacySettings.dataRetentionPeriod)?.label || '2 Years',
            () => setShowDataRetentionModal(true)
          )}

          {renderSwitchSetting(
            'trash-outline',
            '#0e4e96',
            'Automatic Data Deletion',
            'Automatically delete old data after retention period',
            privacySettings.automaticDataDeletion || false,
            (value) => updatePrivacySetting('automaticDataDeletion', value)
          )}

          {renderSwitchSetting(
            'document-text-outline',
            '#0e4e96',
            'Keep Application History',
            'Retain your job application history',
            privacySettings.keepApplicationHistory || false,
            (value) => updatePrivacySetting('keepApplicationHistory', value)
          )}

          {renderSwitchSetting(
            'search-outline',
            '#0e4e96',
            'Retain Search History',
            'Keep your job search history for recommendations',
            privacySettings.retainSearchHistory || false,
            (value) => updatePrivacySetting('retainSearchHistory', value)
          )}
        </View>

        {/* Security & Access Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Security & Access</Text>

          {renderSwitchSetting(
            'shield-outline',
            '#10b981',
            'Two-Factor Authentication',
            'Add an extra layer of security to your account',
            privacySettings.twoFactorAuth || false,
            (value) => updatePrivacySetting('twoFactorAuth', value)
          )}

          {renderSwitchSetting(
            'notifications-outline',
            '#10b981',
            'Login Notifications',
            'Get notified when someone logs into your account',
            privacySettings.loginNotifications || false,
            (value) => updatePrivacySetting('loginNotifications', value)
          )}

          {renderSwitchSetting(
            'warning-outline',
            '#10b981',
            'Suspicious Activity Alerts',
            'Get alerts for unusual account activity',
            privacySettings.suspiciousActivityAlerts || false,
            (value) => updatePrivacySetting('suspiciousActivityAlerts', value)
          )}

          {renderSwitchSetting(
            'list-outline',
            '#10b981',
            'Data Access Logging',
            'Log when your data is accessed by our systems',
            privacySettings.dataAccessLogging || false,
            (value) => updatePrivacySetting('dataAccessLogging', value)
          )}
        </View>

        {/* Cookie & Tracking Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cookies & Tracking</Text>

          {renderSwitchSetting(
            'checkmark-circle-outline',
            '#6b7280',
            'Essential Cookies',
            'Required for basic app functionality (cannot be disabled)',
            true,
            () => {},
            false
          )}

          {renderSwitchSetting(
            'settings-outline',
            '#0e4e96',
            'Functional Cookies',
            'Remember your preferences and settings',
            privacySettings.functionalCookies || false,
            (value) => updatePrivacySetting('functionalCookies', value)
          )}

          {renderSwitchSetting(
            'analytics-outline',
            '#f59e0b',
            'Analytics Cookies',
            'Help us understand how you use the app',
            privacySettings.analyticsCookies || false,
            (value) => updatePrivacySetting('analyticsCookies', value)
          )}

          {renderSwitchSetting(
            'tv-outline',
            '#ef4444',
            'Advertising Cookies',
            'Used to show you relevant advertisements',
            privacySettings.advertisingCookies || false,
            (value) => updatePrivacySetting('advertisingCookies', value),
            true
          )}

          {renderSwitchSetting(
            'link-outline',
            '#ef4444',
            'Cross-Site Tracking',
            'Allow tracking across different websites',
            privacySettings.crossSiteTracking || false,
            (value) => updatePrivacySetting('crossSiteTracking', value),
            true
          )}
        </View>

        {/* Account Privacy Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account Privacy</Text>

          {renderSwitchSetting(
            'search-outline',
            '#0e4e96',
            'Hide from Search Engines',
            'Prevent your profile from appearing in search results',
            privacySettings.hideFromSearchEngines || false,
            (value) => updatePrivacySetting('hideFromSearchEngines', value)
          )}

          {renderSwitchSetting(
            'eye-off-outline',
            '#0e4e96',
            'Anonymize Public Data',
            'Remove identifying information from public data',
            privacySettings.anonymizePublicData || false,
            (value) => updatePrivacySetting('anonymizePublicData', value)
          )}

          {renderSwitchSetting(
            'pause-outline',
            '#0e4e96',
            'Limit Data Processing',
            'Restrict how your data is processed',
            privacySettings.limitDataProcessing || false,
            (value) => updatePrivacySetting('limitDataProcessing', value)
          )}

          {renderSwitchSetting(
            'ban-outline',
            '#0e4e96',
            'Opt Out of Profiling',
            'Prevent automated decision-making based on your data',
            privacySettings.optOutOfProfiling || false,
            (value) => updatePrivacySetting('optOutOfProfiling', value)
          )}
        </View>

        {/* Data Rights Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Data Rights</Text>

          {renderChevronSetting(
            'document-outline',
            '#0e4e96',
            'Generate Privacy Report',
            'Get a detailed report of your privacy settings and data',
            handleGeneratePrivacyReport
          )}

          {renderChevronSetting(
            'download-outline',
            '#0e4e96',
            'Download My Data',
            'Export all your personal data in a portable format',
            () => Alert.alert('Data Export', 'Your data export will be sent to your email within 24 hours.')
          )}

          {renderChevronSetting(
            'trash-outline',
            '#ef4444',
            'Request Data Deletion',
            'Delete specific categories of your personal data',
            () => setShowDataDeletionModal(true)
          )}

          {renderChevronSetting(
            'mail-outline',
            '#0e4e96',
            'Contact Data Protection Officer',
            'Get help with privacy questions and concerns',
            handleContactDataProtectionOfficer
          )}
        </View>

        {/* Legal Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Legal Information</Text>

          {renderChevronSetting(
            'document-text-outline',
            '#6b7280',
            'Privacy Policy',
            'Read our complete privacy policy',
            () => Linking.openURL('https://jobeng.ai/privacy')
          )}

          {renderChevronSetting(
            'shield-checkmark-outline',
            '#6b7280',
            'GDPR Compliance',
            'Learn about our GDPR compliance measures',
            () => Alert.alert('GDPR Compliance', 'JOBENG is fully compliant with GDPR regulations and UK data protection laws.')
          )}

          {renderChevronSetting(
            'information-circle-outline',
            '#6b7280',
            'Data Processing Agreement',
            'View our data processing terms',
            () => Linking.openURL('https://jobeng.ai/dpa')
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Last updated: {new Date().toLocaleDateString()}</Text>
          <Text style={styles.footerSubText}>
            Your privacy is protected by UK and EU data protection laws
          </Text>
        </View>
      </ScrollView>

      {/* Profile Visibility Modal */}
      {renderSelectionModal(
        showProfileVisibilityModal,
        setShowProfileVisibilityModal,
        'Profile Visibility',
        profileVisibilityOptions,
        privacySettings.profileVisibility || 'public',
        (value) => updatePrivacySetting('profileVisibility', value)
      )}

      {/* Data Retention Modal */}
      {renderSelectionModal(
        showDataRetentionModal,
        setShowDataRetentionModal,
        'Data Retention Period',
        dataRetentionOptions,
        privacySettings.dataRetentionPeriod || '2-years',
        (value) => updatePrivacySetting('dataRetentionPeriod', value)
      )}

      {/* Data Deletion Modal */}
      <Modal
        visible={showDataDeletionModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowDataDeletionModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: '#ef4444' }]}>Request Data Deletion</Text>
              <Pressable
                style={styles.closeButton}
                onPress={() => setShowDataDeletionModal(false)}
              >
                <Ionicons name="close" size={24} color="#64748B" />
              </Pressable>
            </View>

            <View style={styles.deletionContent}>
              <Text style={styles.deletionWarning}>
                ⚠️ Select the data categories you want to delete. This action cannot be undone.
              </Text>

              {Object.keys(deletionCategories).map(category => (
                <TouchableOpacity
                  key={category}
                  style={styles.deletionCategory}
                  onPress={() => setDeletionCategories(prev => ({
                    ...prev,
                    [category]: !prev[category]
                  }))}
                >
                  <View style={styles.deletionCategoryContent}>
                    <Text style={styles.deletionCategoryTitle}>
                      {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Text>
                  </View>
                  <View style={[
                    styles.checkbox,
                    deletionCategories[category] && styles.checkboxSelected
                  ]}>
                    {deletionCategories[category] && (
                      <Ionicons name="checkmark" size={16} color="#ffffff" />
                    )}
                  </View>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={styles.deletionSubmitButton}
                onPress={handleDataDeletionRequest}
              >
                <Text style={styles.deletionSubmitButtonText}>Submit Deletion Request</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  header: {
    backgroundColor: '#0e4e96',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 16 : StatusBar.currentHeight + 16,
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  headerRight: {
    width: 40,
    alignItems: 'center',
  },
  container: {
    flex: 1,
  },
  overviewCard: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    padding: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  overviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  overviewTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginLeft: 12,
  },
  overviewText: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    paddingVertical: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f8fafc',
  },
  importantSetting: {
    backgroundColor: '#fef2f2',
  },
  settingIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  settingContent: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 2,
  },
  importantTitle: {
    color: '#ef4444',
  },
  settingDescription: {
    fontSize: 14,
    color: '#64748b',
  },
  settingValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingValue: {
    fontSize: 14,
    color: '#64748b',
    marginRight: 8,
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  footerText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
    textAlign: 'center',
  },
  footerSubText: {
    fontSize: 12,
    color: '#94a3b8',
    textAlign: 'center',
    marginTop: 4,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
  },
  closeButton: {
    padding: 4,
  },
  modalScrollView: {
    maxHeight: 400,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f8fafc',
  },
  modalOptionSelected: {
    backgroundColor: '#f0f9ff',
  },
  modalOptionContent: {
    flex: 1,
  },
  modalOptionText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 2,
  },
  modalOptionTextSelected: {
    color: '#0e4e96',
  },
  modalOptionDescription: {
    fontSize: 14,
    color: '#64748b',
  },
  deletionContent: {
    padding: 20,
  },
  deletionWarning: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ef4444',
    marginBottom: 20,
  },
  deletionCategory: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  deletionCategoryContent: {
    flex: 1,
  },
  deletionCategoryTitle: {
    fontSize: 16,
    color: '#1e293b',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  deletionSubmitButton: {
    backgroundColor: '#ef4444',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  deletionSubmitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});

